import { Copy, Edit3, FolderOpen, LoaderCircle, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { ProjectSummary, ProjectsPort, ProjectsSnapshot } from '@/ports/projects';
import { IconButton } from '../IconButton';
import { Modal } from '../Modal';

type ProjectSyncPhase = 'local-only' | 'pending' | 'syncing' | 'synced' | 'conflict' | 'error';

const projectSyncPhases = new Set<ProjectSyncPhase>(['local-only', 'pending', 'syncing', 'synced', 'conflict', 'error']);

function syncPresentation(project: ProjectSummary) {
  const candidate = (project as ProjectSummary & { readonly syncPhase?: unknown }).syncPhase;
  const phase = typeof candidate === 'string' && projectSyncPhases.has(candidate as ProjectSyncPhase)
    ? candidate as ProjectSyncPhase
    : project.storage === 'cloud' ? 'synced' : 'local-only';

  switch (phase) {
    case 'pending':
    case 'syncing':
      return { kind: 'pending', label: 'Pendiente', description: 'Hay cambios pendientes de sincronización.' } as const;
    case 'conflict':
      return { kind: 'conflict', label: 'Conflicto', description: 'Hay dos revisiones y se necesita elegir cuál conservar.' } as const;
    case 'error':
      return { kind: 'error', label: 'Error', description: 'La última sincronización no se completó.' } as const;
    case 'synced':
      return { kind: 'cloud', label: 'Nube', description: 'El proyecto está sincronizado con la nube.' } as const;
    default:
      return { kind: 'local', label: 'Local', description: 'El proyecto está guardado sólo en este dispositivo.' } as const;
  }
}

export function ProjectsDialog({ projects, snapshot, onClose }: { readonly projects: ProjectsPort; readonly snapshot: ProjectsSnapshot; readonly onClose: () => void }) {
  const [renaming, setRenaming] = useState<string | null>(null);
  const [name, setName] = useState('');
  useEffect(() => { void projects.refresh(); }, [projects]);
  const saveFailure = snapshot.lastSaveResult && snapshot.lastSaveResult.kind !== 'saved'
    ? snapshot.lastSaveResult.message
    : null;
  const visibleError = saveFailure ?? snapshot.error;

  const saveRename = async () => {
    if (!renaming || !name.trim()) return;
    await projects.rename(renaming, name.trim());
    setRenaming(null);
  };

  return (
    <Modal title="Mis proyectos" size="large" onClose={onClose} footer={<button className="primary-action" type="button" onClick={onClose}>Volver al editor</button>}>
      {snapshot.status === 'loading' ? <div className="operation-state"><LoaderCircle className="spin" /><strong>Cargando proyectos...</strong></div> : null}
      {visibleError ? <p className="inline-error cloud-save-result" role="alert">{visibleError}</p> : null}
      {snapshot.conflict ? (
        <div className="conflict-box" role="alert">
          <strong>Hay dos revisiones del proyecto</strong>
          <p>Ninguna se sobrescribirá silenciosamente. Elegí conservar una copia local o usar la revisión de nube.</p>
          <div><button type="button" onClick={() => void projects.resolveConflict('keep-local-copy')}>Conservar copia local</button><button type="button" onClick={() => void projects.resolveConflict('use-cloud-copy')}>Usar copia de nube</button></div>
        </div>
      ) : null}
      <ul className="project-list">
        {snapshot.items.map((project) => {
          const sync = syncPresentation(project);
          return (
            <li key={project.id}>
              <div className="project-thumbnail">{project.thumbnailUrl ? <img src={project.thumbnailUrl} alt="" /> : <span>{project.product === 'tube' ? 'TUBO' : 'STK'}</span>}</div>
              <div className="project-meta">
                {renaming === project.id ? (
                  <div className="inline-rename"><input autoFocus aria-label="Nuevo nombre del proyecto" value={name} onChange={(event) => setName(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && void saveRename()} /><button type="button" onClick={() => void saveRename()}>Guardar</button></div>
                ) : <><strong>{project.name}</strong><div className="project-meta__details"><span className={`project-sync-state project-sync-state--${sync.kind}`} aria-label={`Sincronización: ${sync.label}. ${sync.description}`}>{sync.label}</span><span aria-hidden="true">·</span><time dateTime={project.updatedAt}>{new Date(project.updatedAt).toLocaleDateString('es-AR')}</time></div></>}
              </div>
              <div className="project-actions">
                <IconButton icon={FolderOpen} label="Abrir" onClick={() => void projects.open(project.id).then(onClose)} />
                <IconButton icon={Edit3} label="Renombrar" onClick={() => { setRenaming(project.id); setName(project.name); }} />
                <IconButton icon={Copy} label="Duplicar" onClick={() => void projects.duplicate(project.id)} />
                <IconButton icon={Trash2} label="Eliminar" className="is-danger" onClick={() => { if (window.confirm(`¿Eliminar "${project.name}"?`)) void projects.delete(project.id); }} />
              </div>
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}
